// ColorPickerGrid.jsx
import { h, Component } from 'preact';

import localUtil from '../../lib/util';
import { isGradientValue, splitBackgroundValue } from '../../lib/fieldsHelpers';

// Name used for the synthetic "no color / reset to default" cell. Its hex is the
// empty string, so selecting it clears the field and the app falls back to its
// built-in default color.
export const CLEAR_CELL_NAME = '__clear__';

// Build the list of cells the grid renders. When clearing is allowed, a clear
// cell (empty hex) is prepended so it is always the first swatch in every view.
export const buildCells = (options = [], allowClear = false) => (
    allowClear ? [['', CLEAR_CELL_NAME], ...options] : options.slice()
);

// Index of the cell whose hex matches the given value (case-insensitive). With a
// clear cell present an empty value resolves to index 0 (the clear swatch shows
// the checkmark); without one it returns -1 (nothing selected).
export const indexForValue = (value, cells = []) => (
    cells.findIndex(o => o[0].toLowerCase() === (value || '').toLowerCase())
);

// Style for one swatch button. Values are either '' (clear cell), a solid hex,
// or a gradient stored as '<css>;<fg>' — the fg doubles as the ring/checkmark
// color since luminance can't be computed for a gradient.
export const swatchStyle = (value) => {
    if (value === '') {
        return { color: 'var(--bs-body-color)' };
    }
    if (isGradientValue(value)) {
        const { css, fg } = splitBackgroundValue(value);
        return { background: css, color: fg || '#ffffff' };
    }
    return { backgroundColor: value, color: localUtil.isDark(value) ? '#fff' : '#000' };
};

export default class ColorPickerGrid extends Component {
    constructor(props) {
        super(props);
        this.btnRefs = [];
        const idx = indexForValue(props.value, this.getCells(props));
        this.state = {
            focusedIdx: idx >= 0 ? idx : 0,
            view: 'oneline',
            oneLineLength: 6,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            const idx = indexForValue(this.props.value, this.getCells());
            if (idx >= 0 && idx !== this.state.focusedIdx) {
                this.setState({ focusedIdx: idx });
            }
        }
    }

    getCells = (props = this.props) => buildCells(props.options, props.allowClear);

    setValue = (hex, idx) => {
        this.setState({ focusedIdx: idx });
        if (this.props.onChange) this.props.onChange(hex);
    };

    handleKeyDown = (e, idx) => {
        const cells = this.getCells();
        let next = idx;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % cells.length;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + cells.length) % cells.length;
        if (next !== idx) {
            e.preventDefault();
            const [hex] = cells[next];
            this.setValue(hex, next);
            this.btnRefs[next] && this.btnRefs[next].focus();
        }
    };

    setView = (view) => {
        this.setState({ view });
    }

    renderSwatch = ([hex, name], idx) => {
        const { value, clearLabel = 'No color (default)' } = this.props;
        const isClear = hex === '';
        const selected = (value || '').toLowerCase() === (hex || '').toLowerCase();
        const label = isClear ? clearLabel : name;
        return (
            <button
                key={isClear ? CLEAR_CELL_NAME : hex}
                ref={el => (this.btnRefs[idx] = el)}
                type='button'
                role='radio'
                aria-checked={selected ? 'true' : 'false'}
                tabIndex={selected ? 0 : -1}
                class={`colorSwatch${isClear ? ' colorSwatchClear' : ''}`}
                title={label}
                onClick={() => this.setValue(hex, idx)}
                onKeyDown={e => this.handleKeyDown(e, idx)}
                style={swatchStyle(hex)}
            >
                {isClear
                    ? (selected
                        ? <span class='swatchMark' aria-hidden='true'>✓</span>
                        : <span class='swatchClearMark' aria-hidden='true'>∅</span>)
                    : (selected && <span class='swatchMark' aria-hidden='true'>✓</span>)}
                <span class='visuallyHidden'>{label}</span>
            </button>
        );
    };

    render() {
        const {
            columns = 10,
            label = 'Choose color',
            moreLabel = '',
        } = this.props;
        const { view, oneLineLength } = this.state;
        const cells = this.getCells();

        if (view === 'oneline') {
            // Show a single line with the first colors and a button to expand:
            return (<>
                <div class='colorGrid colorGridOneline' role='radiogroup' aria-label={label} style={{ '--columns': String(Math.min(oneLineLength + 1, cells.length)) }}>
                    {cells.slice(0, oneLineLength).map((cell, idx) => this.renderSwatch(cell, idx))}
                    {cells.length > oneLineLength && (
                        <button
                            type='button'
                            class={`colorSwatch colorSwatchMore ${moreLabel ? 'colorSwatchMoreLabel' : ''}`}
                            title='Show all colors'
                            onClick={() => this.setView('grid')}
                        >
                            <span aria-hidden={moreLabel ? undefined : 'true'}>{moreLabel || '+'}</span>
                            <span class='visuallyHidden'>Show all colors</span>
                        </button>
                    )}
                </div>
            </>);
        }

        return (<>
            <div
                class='colorGrid'
                role='radiogroup'
                aria-label={label}
                style={{ '--columns': String(columns) }}
            >
                {cells.map((cell, idx) => this.renderSwatch(cell, idx))}
            </div>
        </>);
    }
}
